import { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  downloadRisksTemplate,
  importRisksPreview,
  importRisksWorkbook,
} from './risksApi';
import type { RiskImportPreview, RiskImportResult } from './types';
import {
  RISK_IMPORT_FORMAT_LABEL,
  RISK_IMPORT_SEVERITY_LABEL,
  riskImportCountLabel,
} from './riskUtils';

interface RiskImportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported?: () => void;
}

export function RiskImportModal({ open, onOpenChange, onImported }: RiskImportModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<RiskImportPreview | null>(null);
  const [result, setResult] = useState<RiskImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<'fq22' | 'fgl05' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleFileChange = (selected: File | null) => {
    setFile(selected);
    setPreview(null);
    setResult(null);
    setError(null);
  };

  const runPreview = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const data = await importRisksPreview(file);
      setPreview(data);
    } catch {
      setError('No se pudo previsualizar el archivo. Verifique que sea F-GL-05 o FQ-22.');
    } finally {
      setLoading(false);
    }
  };

  const runImport = async () => {
    if (!file || !preview?.canImport) return;
    setLoading(true);
    setError(null);
    try {
      const data = await importRisksWorkbook(file);
      setResult(data);
      onImported?.();
    } catch {
      setError('Error al importar. Corrija los errores de la previsualización e intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async (format: 'fq22' | 'fgl05') => {
    setDownloading(format);
    setError(null);
    try {
      await downloadRisksTemplate(format);
    } catch {
      setError('No se pudo descargar la plantilla de ejemplo.');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Importar planilla (IMX)</DialogTitle>
        </DialogHeader>

        <div className='space-y-4'>
          <p className='text-sm text-muted-foreground'>
            Compatible con F-GL-05 (estratégico) y FQ-22 (IPER/AMFE). La previsualización muestra
            diferencias y errores antes de importar.
          </p>

          <div className='rounded-md border bg-muted/30 p-3 space-y-2'>
            <p className='text-sm font-medium'>Plantillas de ejemplo</p>
            <p className='text-xs text-muted-foreground'>
              Descargue un Excel con la estructura y datos de muestra que espera la importación.
            </p>
            <div className='flex flex-wrap gap-2'>
              <button
                type='button'
                disabled={downloading !== null}
                onClick={() => void handleDownloadTemplate('fq22')}
                className='px-3 py-1.5 text-sm rounded-md border bg-background hover:bg-muted disabled:opacity-50'
              >
                {downloading === 'fq22' ? 'Descargando…' : 'FQ-22 (operativo / IPER)'}
              </button>
              <button
                type='button'
                disabled={downloading !== null}
                onClick={() => void handleDownloadTemplate('fgl05')}
                className='px-3 py-1.5 text-sm rounded-md border bg-background hover:bg-muted disabled:opacity-50'
              >
                {downloading === 'fgl05' ? 'Descargando…' : 'F-GL-05 (estratégico)'}
              </button>
            </div>
          </div>

          <input
            ref={inputRef}
            type='file'
            accept='.xlsx,.xlsm'
            onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
            className='block w-full text-sm'
          />

          {error && <p className='text-sm text-destructive'>{error}</p>}

          {preview && (
            <div className='rounded-md border p-3 space-y-2 text-sm'>
              <p>
                <span className='font-medium'>Formato:</span>{' '}
                {RISK_IMPORT_FORMAT_LABEL[preview.format] ?? preview.format}
              </p>
              <p>
                <span className='font-medium'>Errores:</span> {preview.summary.errorCount} ·{' '}
                <span className='font-medium'>Advertencias:</span> {preview.summary.warningCount}
              </p>
              <ul className='list-disc pl-5'>
                {Object.entries(preview.summary.counts).map(([key, value]) => (
                  <li key={key}>
                    {riskImportCountLabel(key)}: {String(value)}
                  </li>
                ))}
              </ul>
              {preview.issues.length > 0 && (
                <div className='max-h-40 overflow-y-auto border-t pt-2'>
                  {preview.issues.slice(0, 50).map((issue: RiskImportPreview['issues'][number], idx: number) => (
                    <p
                      key={`${issue.sheet}-${issue.row}-${idx}`}
                      className={
                        issue.severity === 'error' ? 'text-destructive' : 'text-muted-foreground'
                      }
                    >
                      [{RISK_IMPORT_SEVERITY_LABEL[issue.severity] ?? issue.severity}]{' '}
                      {issue.sheet} fila {issue.row}: {issue.message}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {result && (
            <div className='rounded-md border border-green-600/30 bg-green-50 dark:bg-green-950/20 p-3 text-sm space-y-2'>
              <p className='font-medium'>Importación completada</p>
              {Object.keys(result.created).length > 0 && (
                <div>
                  <p className='font-medium text-xs uppercase tracking-wide text-muted-foreground'>
                    Creados
                  </p>
                  <ul className='list-disc pl-5 mt-1'>
                    {Object.entries(result.created).map(([key, value]) => (
                      <li key={`created-${key}`}>
                        {riskImportCountLabel(key)}: {value}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {Object.keys(result.skipped).length > 0 && (
                <div>
                  <p className='font-medium text-xs uppercase tracking-wide text-muted-foreground'>
                    Omitidos (ya existían)
                  </p>
                  <ul className='list-disc pl-5 mt-1'>
                    {Object.entries(result.skipped).map(([key, value]) => (
                      <li key={`skipped-${key}`}>
                        {riskImportCountLabel(key)}: {value}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className='flex justify-end gap-2'>
            <button
              type='button'
              onClick={() => handleClose(false)}
              className='px-4 py-2 text-sm rounded-md border'
            >
              Cerrar
            </button>
            <button
              type='button'
              disabled={!file || loading}
              onClick={() => void runPreview()}
              className='px-4 py-2 text-sm rounded-md border disabled:opacity-50'
            >
              {loading ? 'Procesando…' : 'Previsualizar'}
            </button>
            <button
              type='button'
              disabled={!preview?.canImport || loading || !!result}
              onClick={() => void runImport()}
              className='px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground disabled:opacity-50'
            >
              Importar
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
